export type ClosureSearchMode = 'STANDARD' | 'THIRTEEN_PERIOD';

export type ProjectionTarget = 'ngrx-gst-filing' | 'user-preferences';
export type ProjectionMode = 'sync' | 'persist' | 'hydrate';

export interface ContextBinding {
    ref: string;
    required?: boolean;
    source?: 'runtime' | 'selector' | 'service';
    description?: string;
}

export interface CatalogOption {
    label: string;
    value: string;
    description?: string;
    datatype?: string;
    tagEntries?: { key: string; value: string }[];
}

export interface CatalogDefinition {
    id: string;
    type: 'select-options' | 'lookup';
    options: CatalogOption[];
}

export interface VisibilityRule {
    all?: { ref: string; operator: 'eq' | 'neq'; value: string | boolean }[];
    any?: { ref: string; operator: 'eq' | 'neq'; value: string | boolean }[];
}

export interface FormatDerivationRule {
    // Node id (within the same flow) whose value should be auto-derived, e.g. the "Year Format" select node.
    targetNodeId: string;
    // Tag keys to look up on the selected field, in priority order (e.g. explicit 'format' tag wins over legacy 'gst-filing-field').
    tagKeyPriority: string[];
    // Maps a legacy tag value (e.g. gst-filing-field="period") to the target catalog's option value (e.g. "yyyy-yy").
    // Not used for the 'format' tag key, whose value is expected to match a target catalog option value directly.
    valueMap?: Record<string, string>;
}

export interface FlowNode {
    kind: 'select' | 'text' | 'preview' | 'group';
    id: string;
    label: string;
    persistAs?: string;
    placeholder?: string;
    catalogId?: string;
    template?: string;
    dependsOn?: string[];
    visibleWhen?: VisibilityRule;
    disabled?: boolean;
    nodes?: FlowNode[];
    required?: boolean;
    formatDerivation?: FormatDerivationRule;
}

export interface FlowSection {
    id: string;
    title: string;
    nodes: FlowNode[];
}

export interface FlowDefinition {
    id: string;
    title: string;
    sections: FlowSection[];
}

export interface DerivationDefinition {
    id: string;
    strategy: string;
    inputs: string[];
    output: string;
}

export interface ProjectionDefinition {
    id: string;
    target: ProjectionTarget;
    mode: ProjectionMode;
    envelope?: string;
    pathTemplate: string;
    keyRefs: string[];
    include: string[];
}

export interface ClosureSearchGrammar {
    id: string;
    title: string;
    contextBindings: ContextBinding[];
    catalogs: CatalogDefinition[];
    flows: FlowDefinition[];
    derivations: DerivationDefinition[];
    projections: ProjectionDefinition[];
}

const modeCatalog: CatalogDefinition = {
    id: 'catalog.searchMode',
    type: 'select-options',
    options: [
        {
            label: 'Standard (Year / Month / Quarter)',
            value: 'STANDARD',
            description: 'Use default financial year selectors.',
        },
        {
            label: '13-Period Calendar',
            value: 'THIRTEEN_PERIOD',
            description: 'Use custom period set with explicit date range.',
        },
    ],
};

const primarySearchableFieldsCatalog: CatalogDefinition = {
    id: 'catalog.primarySearchableFields',
    type: 'select-options',
    options: [],
};

const yearFormatsCatalog: CatalogDefinition = {
    id: 'catalog.yearFormats',
    type: 'select-options',
    options: [
        { label: 'YYYY (4-digit year ex: 2026)', value: 'yyyy' },
        { label: 'YYYY-YY (Financial year ex: 2025-26)', value: 'yyyy-yy' },
        { label: 'YYYYYY (6-digit ex: 202526)', value: 'yyyyyy' },
        { label: 'MM-YYYY (Month-Year ex: 03-2026)', value: 'mm-yyyy' },
        { label: 'MMYYYY (Month-Year no hyphen ex: 032026)', value: 'mmyyyy' },
    ],
};

const monthFormatsCatalog: CatalogDefinition = {
    id: 'catalog.monthFormats',
    type: 'select-options',
    options: [{ label: 'MM (2-digit month ex: 03)', value: 'mm' }],
};

const quarterFormatsCatalog: CatalogDefinition = {
    id: 'catalog.quarterFormats',
    type: 'select-options',
    options: [{ label: 'Q1, Q2, Q3, Q4 (ex: Q1)', value: 'Q' }],
};

const dateFieldsCatalog: CatalogDefinition = {
    id: 'catalog.dateFields',
    type: 'select-options',
    options: [],
};

// Maps legacy 'gst-filing-field' tag values (see UtilsService.getValueByTagAndFieldFormat) to the
// corresponding 'catalog.yearFormats' option value, used to auto-derive the Year Format selection.
const yearFieldTagValueMap: Record<string, string> = {
    year: 'yyyy',
    period: 'yyyy-yy',
    'period-without-hyphen': 'yyyyyy',
    'month-year': 'mm-yyyy',
    monthyear: 'mmyyyy',
};

// Maps legacy 'gst-filing-field' tag values to the corresponding 'catalog.monthFormats' option value.
const monthFieldTagValueMap: Record<string, string> = {
    month: 'mm',
};

// Maps legacy 'gst-filing-field' tag values to the corresponding 'catalog.quarterFormats' option value.
const quarterFieldTagValueMap: Record<string, string> = {
    quarter: 'Q',
};

// Tag keys are checked in this order: an explicit 'format' tag (new) always wins over the legacy
// 'gst-filing-field' tag, preserving backward compatibility for existing apps/tags.
const formatTagKeyPriority = ['format', 'gst-filing-field'];

const setupFlow: FlowDefinition = {
    id: 'setup',
    title: 'Closure Setup',
    sections: [
        {
            id: 'setup.pattern',
            title: 'Search Pattern',
            nodes: [
                {
                    kind: 'select',
                    id: 'setup.pattern.mode',
                    label: 'Mode',
                    catalogId: 'catalog.searchMode',
                    persistAs: 'pattern.mode',
                    required: true,
                },
            ],
        },
        {
            id: 'setup.calendar',
            title: 'Calendar Rules',
            nodes: [
                {
                    kind: 'select',
                    id: 'setup.calendar.financialYearStartMonth',
                    label: 'Financial Year Start Month',
                    persistAs: 'calendar.financialYearStartMonth',
                    placeholder: 'Select start month',
                },
            ],
        },
        {
            id: 'setup.mapping',
            title: 'Entity Mapping',
            nodes: [
                {
                    kind: 'group',
                    id: 'setup.mapping.standard.yearSection',
                    label: 'Year Section',
                    visibleWhen: {
                        all: [{ ref: 'setup.pattern.mode', operator: 'eq', value: 'STANDARD' }],
                    },
                    nodes: [
                        {
                            kind: 'select',
                            id: 'setup.mapping.standard.yearField',
                            label: 'Year Field',
                            catalogId: 'catalog.primarySearchableFields',
                            persistAs: 'mapping.standard.yearField',
                            placeholder: 'Select year field',
                            formatDerivation: {
                                targetNodeId: 'setup.mapping.standard.yearFormat',
                                tagKeyPriority: formatTagKeyPriority,
                                valueMap: yearFieldTagValueMap,
                            },
                        },
                        {
                            kind: 'text',
                            id: 'setup.mapping.standard.yearFieldDatatype',
                            label: 'Year Field Data Type',
                            persistAs: 'mapping.standard.yearFieldDatatype',
                            disabled: true,
                        },
                        {
                            kind: 'select',
                            id: 'setup.mapping.standard.yearFormat',
                            label: 'Year Format',
                            catalogId: 'catalog.yearFormats',
                            persistAs: 'mapping.standard.yearFormat',
                            placeholder: 'Select format',
                            visibleWhen: {
                                all: [{ ref: 'setup.mapping.standard.yearFieldDatatype', operator: 'eq', value: 'STRING' }],
                            },
                        },
                    ],
                },
                {
                    kind: 'group',
                    id: 'setup.mapping.standard.monthSection',
                    label: 'Month Section',
                    visibleWhen: {
                        all: [{ ref: 'setup.pattern.mode', operator: 'eq', value: 'STANDARD' }],
                    },
                    nodes: [
                        {
                            kind: 'select',
                            id: 'setup.mapping.standard.monthField',
                            label: 'Month Field',
                            catalogId: 'catalog.primarySearchableFields',
                            persistAs: 'mapping.standard.monthField',
                            placeholder: 'Select month field',
                            formatDerivation: {
                                targetNodeId: 'setup.mapping.standard.monthFormat',
                                tagKeyPriority: formatTagKeyPriority,
                                valueMap: monthFieldTagValueMap,
                            },
                        },
                        {
                            kind: 'text',
                            id: 'setup.mapping.standard.monthFieldDatatype',
                            label: 'Month Field Data Type',
                            persistAs: 'mapping.standard.monthFieldDatatype',
                            disabled: true,
                        },
                        {
                            kind: 'select',
                            id: 'setup.mapping.standard.monthFormat',
                            label: 'Month Format',
                            catalogId: 'catalog.monthFormats',
                            persistAs: 'mapping.standard.monthFormat',
                            placeholder: 'Select format',
                            visibleWhen: {
                                all: [{ ref: 'setup.mapping.standard.monthFieldDatatype', operator: 'eq', value: 'STRING' }],
                            },
                        },
                    ],
                },
                {
                    kind: 'group',
                    id: 'setup.mapping.standard.quarterSection',
                    label: 'Quarter Section',
                    visibleWhen: {
                        all: [{ ref: 'setup.pattern.mode', operator: 'eq', value: 'STANDARD' }],
                    },
                    nodes: [
                        {
                            kind: 'select',
                            id: 'setup.mapping.standard.quarterField',
                            label: 'Quarter Field',
                            catalogId: 'catalog.primarySearchableFields',
                            persistAs: 'mapping.standard.quarterField',
                            placeholder: 'Select quarter field',
                            formatDerivation: {
                                targetNodeId: 'setup.mapping.standard.quarterFormat',
                                tagKeyPriority: formatTagKeyPriority,
                                valueMap: quarterFieldTagValueMap,
                            },
                        },
                        {
                            kind: 'text',
                            id: 'setup.mapping.standard.quarterFieldDatatype',
                            label: 'Quarter Field Data Type',
                            persistAs: 'mapping.standard.quarterFieldDatatype',
                            disabled: true,
                        },
                        {
                            kind: 'select',
                            id: 'setup.mapping.standard.quarterFormat',
                            label: 'Quarter Format',
                            catalogId: 'catalog.quarterFormats',
                            persistAs: 'mapping.standard.quarterFormat',
                            placeholder: 'Select format',
                            visibleWhen: {
                                all: [{ ref: 'setup.mapping.standard.quarterFieldDatatype', operator: 'eq', value: 'STRING' }],
                            },
                        },
                    ],
                },
                {
                    kind: 'group',
                    id: 'setup.mapping.period.section',
                    label: '13-Period Calendar',
                    visibleWhen: {
                        all: [{ ref: 'setup.pattern.mode', operator: 'eq', value: 'THIRTEEN_PERIOD' }],
                    },
                    nodes: [
                        {
                            kind: 'select',
                            id: 'setup.mapping.period.dateFields',
                            label: 'Date Field',
                            catalogId: 'catalog.primarySearchableFields',
                            persistAs: 'mapping.period.dateFields',
                            placeholder: 'Select date field (DATE type only)',
                            required: true,
                        },
                    ],
                },
            ],
        },
        {
            id: 'setup.preview',
            title: 'Preview',
            nodes: [
                {
                    kind: 'preview',
                    id: 'setup.preview.quarterTemplate',
                    label: 'Standard Preview',
                    persistAs: 'preview.quarterTemplate',
                    template: 'FY {{mapping.standard.yearField}} • {{mapping.standard.monthField}} • {{mapping.standard.quarterField}}',
                    dependsOn: ['mapping.standard.yearField', 'mapping.standard.monthField', 'mapping.standard.quarterField'],
                    visibleWhen: {
                        all: [{ ref: 'setup.pattern.mode', operator: 'eq', value: 'STANDARD' }],
                    },
                },
                {
                    kind: 'preview',
                    id: 'setup.preview.periodTemplate',
                    label: '13-Period Preview',
                    persistAs: 'preview.periodTemplate',
                    template: '{{mapping.period.dateFields}} • P-001 to P-013',
                    dependsOn: ['mapping.period.dateFields'],
                    visibleWhen: {
                        all: [{ ref: 'setup.pattern.mode', operator: 'eq', value: 'THIRTEEN_PERIOD' }],
                    },
                },
            ],
        },
    ],
};

const requestFlow: FlowDefinition = {
    id: 'request',
    title: 'Closure Request',
    sections: [
        {
            id: 'request.selection',
            title: 'Runtime Selection',
            nodes: [
                {
                    kind: 'select',
                    id: 'request.selection.mode',
                    label: 'Mode',
                    catalogId: 'catalog.searchMode',
                    persistAs: 'selection.mode',
                },
                {
                    kind: 'group',
                    id: 'request.selection.standard',
                    label: 'Standard Selection',
                    visibleWhen: {
                        all: [{ ref: 'request.selection.mode', operator: 'eq', value: 'STANDARD' }],
                    },
                    nodes: [
                        {
                            kind: 'text',
                            id: 'request.selection.standard.year',
                            label: 'Year',
                            persistAs: 'selection.standard.year',
                        },
                        {
                            kind: 'text',
                            id: 'request.selection.standard.month',
                            label: 'Month',
                            persistAs: 'selection.standard.month',
                        },
                        {
                            kind: 'text',
                            id: 'request.selection.standard.quarter',
                            label: 'Quarter',
                            persistAs: 'selection.standard.quarter',
                        },
                    ],
                },
                {
                    kind: 'group',
                    id: 'request.selection.period13',
                    label: '13-Period Selection',
                    visibleWhen: {
                        all: [{ ref: 'request.selection.mode', operator: 'eq', value: 'THIRTEEN_PERIOD' }],
                    },
                    nodes: [
                        {
                            kind: 'text',
                            id: 'request.selection.period13.year',
                            label: 'Year',
                            persistAs: 'selection.period13.year',
                        },
                        {
                            kind: 'text',
                            id: 'request.selection.period13.period',
                            label: 'Period',
                            persistAs: 'selection.period13.period',
                        },
                    ],
                },
                {
                    kind: 'preview',
                    id: 'request.preview.standard',
                    label: 'Period Label',
                    template: 'FY {{selection.standard.year}} • {{selection.standard.month}} • {{selection.standard.quarter}}',
                    dependsOn: ['selection.standard.year', 'selection.standard.month', 'selection.standard.quarter'],
                    visibleWhen: {
                        all: [{ ref: 'request.selection.mode', operator: 'eq', value: 'STANDARD' }],
                    },
                },
                {
                    kind: 'preview',
                    id: 'request.preview.period13',
                    label: 'Period Label',
                    template: 'FY {{selection.period13.year}} • Period {{selection.period13.period}}',
                    dependsOn: ['selection.period13.year', 'selection.period13.period'],
                    visibleWhen: {
                        all: [{ ref: 'request.selection.mode', operator: 'eq', value: 'THIRTEEN_PERIOD' }],
                    },
                },
            ],
        },
    ],
};

export const CLOSURE_SEARCH_GRAMMAR: ClosureSearchGrammar = {
    id: 'closure-search-grammar',
    title: 'Closure Search Grammar',
    contextBindings: [
        { ref: 'context.userId', required: true, source: 'selector', description: 'Current logged-in user id.' },
        { ref: 'context.appRestApiName', required: true, source: 'selector', description: 'Current app REST API name.' },
        { ref: 'context.serviceId', required: true, source: 'selector', description: 'Current app service id.' },
    ],
    catalogs: [modeCatalog, primarySearchableFieldsCatalog, yearFormatsCatalog, monthFormatsCatalog, quarterFormatsCatalog],
    flows: [setupFlow, requestFlow],
    derivations: [
        {
            id: 'resolveStandardRange',
            strategy: 'resolve-standard-date-range',
            inputs: [
                'selection.standard.year',
                'selection.standard.month',
                'selection.standard.quarter',
                'calendar.financialYearStartMonth',
            ],
            output: 'derived.standardRange',
        },
        {
            id: 'resolvePeriod13Range',
            strategy: 'resolve-13period-date-range',
            inputs: ['selection.period13.year', 'selection.period13.period', 'mapping.period.dateFields'],
            output: 'derived.period13Range',
        },
        {
            id: 'quarterPreview',
            strategy: 'render-preview',
            inputs: ['selection.standard.year', 'selection.standard.month', 'selection.standard.quarter', 'derived.standardRange'],
            output: 'resolvedQuarterRange',
        },
        {
            id: 'periodPreview',
            strategy: 'render-preview',
            inputs: ['selection.period13.year', 'selection.period13.period', 'derived.period13Range'],
            output: 'resolvedPeriodRange',
        },
    ],
    projections: [
        {
            id: 'ngrx-closure-sync',
            target: 'ngrx-gst-filing',
            mode: 'sync',
            pathTemplate: 'gstFiling.closure.apps.{context.serviceId}',
            keyRefs: ['context.serviceId'],
            include: [
                'calendar.financialYearStartMonth',
                'calendar.quarterStartMonth',
                'pattern.mode',
                'pattern.standard.variables',
                'pattern.period.yearRequired',
                'mapping.standard.strategy',
                'mapping.standard.yearField',
                'mapping.standard.yearFieldDatatype',
                'mapping.standard.yearFormat',
                'mapping.standard.monthField',
                'mapping.standard.monthFieldDatatype',
                'mapping.standard.monthFormat',
                'mapping.standard.quarterField',
                'mapping.standard.quarterFieldDatatype',
                'mapping.standard.quarterFormat',
                'mapping.standard.dateFields',
                'mapping.period.dateFields',
                'preview.quarterTemplate',
                'preview.periodTemplate',
                'selection.mode',
                'selection.standard.year',
                'selection.standard.month',
                'selection.standard.quarter',
                'selection.period13.year',
                'selection.period13.period',
            ],
        },
        {
            id: 'user-preferences-closure',
            target: 'user-preferences',
            mode: 'persist',
            envelope: 'user_preferences.{context.userId}',
            pathTemplate: 'apps.{context.appRestApiName}.closure',
            keyRefs: ['context.userId', 'context.appRestApiName'],
            include: [
                'calendar.financialYearStartMonth',
                'calendar.quarterStartMonth',
                'pattern.mode',
                'pattern.standard.variables',
                'pattern.period.yearRequired',
                'mapping.standard.strategy',
                'mapping.standard.yearField',
                'mapping.standard.yearFieldDatatype',
                'mapping.standard.yearFormat',
                'mapping.standard.monthField',
                'mapping.standard.monthFieldDatatype',
                'mapping.standard.monthFormat',
                'mapping.standard.quarterField',
                'mapping.standard.quarterFieldDatatype',
                'mapping.standard.quarterFormat',
                'mapping.standard.dateFields',
                'mapping.period.dateFields',
                'preview.quarterTemplate',
                'preview.periodTemplate',
                'selection.mode',
                'selection.standard.year',
                'selection.standard.month',
                'selection.standard.quarter',
                'selection.period13.year',
                'selection.period13.period',
            ],
        },
        {
            id: 'user-preferences-closure-hydrate',
            target: 'user-preferences',
            mode: 'hydrate',
            envelope: 'user_preferences.{context.userId}',
            pathTemplate: 'apps.{context.appRestApiName}.closure',
            keyRefs: ['context.userId', 'context.appRestApiName'],
            include: [
                'calendar.financialYearStartMonth',
                'calendar.quarterStartMonth',
                'pattern.mode',
                'pattern.standard.variables',
                'pattern.period.yearRequired',
                'mapping.standard.strategy',
                'mapping.standard.yearField',
                'mapping.standard.yearFieldDatatype',
                'mapping.standard.yearFormat',
                'mapping.standard.monthField',
                'mapping.standard.monthFieldDatatype',
                'mapping.standard.monthFormat',
                'mapping.standard.quarterField',
                'mapping.standard.quarterFieldDatatype',
                'mapping.standard.quarterFormat',
                'mapping.standard.dateFields',
                'mapping.period.dateFields',
                'preview.quarterTemplate',
                'preview.periodTemplate',
                'selection.mode',
                'selection.standard.year',
                'selection.standard.month',
                'selection.standard.quarter',
                'selection.period13.year',
                'selection.period13.period',
            ],
        },
    ],
};

export const catalogs = [
    modeCatalog,
    primarySearchableFieldsCatalog,
    dateFieldsCatalog,
    yearFormatsCatalog,
    monthFormatsCatalog,
    quarterFormatsCatalog,
];
